import { PHASE_HEALTH_FAULTS } from '../../../../../constants';
import { getServiceHoursRemaining } from '../../../../../helpers/getServiceHoursRemaining';
import { getWearPercent } from '../../../../../helpers/getWearPercent';
import type {
    Drone,
    IntervalUnit,
    Phase,
    ServiceIntervalConfig,
    ServiceIntervalMap
} from '../../../../../types';
import {
    FLOW_RATE_SUFFIX,
    K_FACTOR_DRIFT_THRESHOLD,
    K_FACTOR_PREFIX,
    MOTOR_OVERTEMP_CELSIUS,
    WEAR_DUE_PERCENT,
    WEAR_UNCONFIGURED_VALUE
} from '../constants';
import type { RowData } from '../types';

type UnitLabelMap = Record<IntervalUnit, string>;

type UnitRowStrings = {
    drift: string;
    due: string;
    flowMeterLabel: string;
    motorLabel: string;
    ok: string;
    overdue: string;
    overtemp: string;
    pumpLabel: string;
    toServiceSuffix: string;
    unitLabels: UnitLabelMap;
};

type UnitRowGroups = {
    flowMeterRows: RowData[];
    motorRows: RowData[];
    pumpRows: RowData[];
};

// Phase-1 countdown: "{remaining} {unit} to service" (e.g. "40 h to service"),
// or "Overdue" once usage reaches the interval. The remaining-% stays on the rings.
const getCountdownValue = ({
    interval,
    strings,
    usage
}: {
    interval?: ServiceIntervalConfig;
    strings: UnitRowStrings;
    usage: number;
}): string => {
    const serviceHours = getServiceHoursRemaining({ interval, usage });

    if (!serviceHours.configured) {
        return WEAR_UNCONFIGURED_VALUE;
    }

    if (serviceHours.overdue) {
        return strings.overdue;
    }

    return `${serviceHours.remaining} ${strings.unitLabels[serviceHours.unit]} ${strings.toServiceSuffix}`;
};

const isDue = ({
    interval,
    usage
}: {
    interval?: ServiceIntervalConfig;
    usage: number;
}): boolean => {
    const wear = getWearPercent({ interval, usage });

    return wear.configured && wear.percentage >= WEAR_DUE_PERCENT;
};

export const getUnitRows = ({
    drone,
    phase,
    serviceIntervals,
    strings
}: {
    drone: Drone;
    phase: Phase;
    serviceIntervals: ServiceIntervalMap;
    strings: UnitRowStrings;
}): UnitRowGroups => {
    const isLive = phase >= PHASE_HEALTH_FAULTS;

    const motorRows = drone.motors.map((motor, index) => {
        const due = isDue({ interval: serviceIntervals.motor, usage: motor.usage });
        const overtemp = isLive && motor.tempC >= MOTOR_OVERTEMP_CELSIUS;
        const status = overtemp ? strings.overtemp : due ? strings.due : strings.ok;
        const value = isLive
            ? `${motor.tempC}°C · ${motor.currentA}A`
            : getCountdownValue({
                  interval: serviceIntervals.motor,
                  strings,
                  usage: motor.usage
              });

        return { label: `${strings.motorLabel} ${index + 1}`, status, value };
    });

    const pumpRows = drone.pumps.map((pump, index) => {
        const due = isDue({ interval: serviceIntervals.pumpSealKit, usage: pump.usage });
        const drift = isLive && pump.flowKFactor >= K_FACTOR_DRIFT_THRESHOLD;
        const status = drift ? strings.drift : due ? strings.due : strings.ok;
        const value = isLive
            ? `${K_FACTOR_PREFIX}${pump.flowKFactor} · ${pump.flowRate}${FLOW_RATE_SUFFIX}`
            : getCountdownValue({
                  interval: serviceIntervals.pumpSealKit,
                  strings,
                  usage: pump.usage
              });

        return { label: `${strings.pumpLabel} ${index + 1}`, status, value };
    });

    const flowMeterDue = isDue({
        interval: serviceIntervals.flowMeter,
        usage: drone.flowMeter.usage
    });
    const flowMeterValue = isLive
        ? `${drone.flowMeter.flowRate}${FLOW_RATE_SUFFIX}`
        : getCountdownValue({
              interval: serviceIntervals.flowMeter,
              strings,
              usage: drone.flowMeter.usage
          });
    const flowMeterRows = [
        {
            label: strings.flowMeterLabel,
            status: flowMeterDue ? strings.due : strings.ok,
            value: flowMeterValue
        }
    ];

    return { flowMeterRows, motorRows, pumpRows };
};
